/**
 * LPC-10289: Custom hook for C2DS field error handling
 *
 * Provides C2DS error state and auto-clear functionality for form fields.
 * When a field value changes, the corresponding C2DS error is automatically cleared.
 */

import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { C2DSFieldKey } from "@investorRequest/models";
import { c2dsErrorsSelector } from "@investorRequest/store/selectors";
import { clearC2DSFieldError } from "@investorRequest/store/ducks";
import { getC2DSErrorDisplayMessage } from "@investorRequest/utils/c2dsErrorParser";

interface UseC2DSFieldErrorResult {
  /** Whether the field has a C2DS error */
  hasError: boolean;
  /** The formatted error message to display (e.g., "Incorrect Routing Number") */
  errorMessage: string;
  /** Call this when the field value changes to clear the C2DS error */
  clearError: () => void;
}

/**
 * Hook to manage C2DS validation errors for a specific form field.
 *
 * @param fieldKey - The C2DS field key to check for errors
 * @returns Object containing error state and clear function
 *
 * @example
 * const { hasError, errorMessage, clearError } = useC2DSFieldError("routingNumber");
 *
 * // In onChange handler:
 * const handleChange = (value: string) => {
 *   clearError(); // Clear C2DS error when user modifies field
 *   onChange("routingNumber", value);
 * };
 *
 * // In render:
 * {hasError && <Box className="ir-helper-text-warning">{errorMessage}</Box>}
 */
export function useC2DSFieldError(fieldKey: C2DSFieldKey): UseC2DSFieldErrorResult {
  const dispatch = useAppDispatch();
  const c2dsErrors = useAppSelector(c2dsErrorsSelector);

  const fieldError = c2dsErrors ? c2dsErrors[fieldKey] : null;
  const hasError = fieldError !== null;
  const errorMessage = getC2DSErrorDisplayMessage(fieldError);

  const clearError = useCallback(() => {
    if (hasError) {
      dispatch(clearC2DSFieldError(fieldKey));
    }
  }, [dispatch, fieldKey, hasError]);

  return {
    hasError,
    errorMessage,
    clearError,
  };
}

/**
 * Hook to get all C2DS errors at once (useful for components that handle multiple fields)
 *
 * @returns Object containing all C2DS errors and a function to clear specific errors
 */
export function useC2DSErrors() {
  const dispatch = useAppDispatch();
  const c2dsErrors = useAppSelector(c2dsErrorsSelector);

  const clearFieldError = useCallback(
    (fieldKey: C2DSFieldKey) => {
      dispatch(clearC2DSFieldError(fieldKey));
    },
    [dispatch]
  );

  // LPC-10289: Direct function calls without useCallback to ensure fresh values
  const getFieldError = (fieldKey: C2DSFieldKey) => {
    if (!c2dsErrors) return null;

    return c2dsErrors[fieldKey];
  };

  const getFieldErrorMessage = (fieldKey: C2DSFieldKey) => {
    if (!c2dsErrors) return "";

    return getC2DSErrorDisplayMessage(c2dsErrors[fieldKey]);
  };

  const hasFieldError = (fieldKey: C2DSFieldKey) => {
    if (!c2dsErrors) return false;

    return c2dsErrors[fieldKey] !== null;
  };

  return {
    c2dsErrors,
    clearFieldError,
    getFieldError,
    getFieldErrorMessage,
    hasFieldError,
  };
}
